import './SearchInput.scss';
import * as React from 'react';
import * as ReactAutocomplete from 'react-autocomplete';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { bind } from 'lodash-decorators/bind';
import { getClasses } from 'src/utils/classFunctions';
import { classList } from 'src/utils/classList';
import { identity } from 'src/utils/identity';
import { IconSearch } from 'src/components/Icon/IconSearch';

type Props = {
    readonly total: number | null;
    readonly value: string;
    readonly onSubmit: (query: string) => void;
    readonly onChange: (query: string) => void;
    readonly suggestions: ReadonlyArray<string>;
    readonly modificators?: ReadonlyArray<string>;
};

export class SearchInput extends React.Component<Props> {
    private inputRef: React.RefObject<ReactAutocomplete>;
    constructor(props: Props) {
        super(props);
        this.inputRef = React.createRef();
    }
    public render(): React.ReactNode {
        const { value, total, suggestions, modificators = [] } = this.props;

        return (
            <div className={classList(getClasses('lvo-search-input', modificators))}>
                <form onSubmit={this.handleSearchSubmit}>
                    <div className="lvo-search-input__search">
                        <span className="lvo-search-input__search-btn">
                            <div className="icon-box">
                                <IconSearch/>
                            </div>
                        </span>
                        <FormattedMessage id="searchInput_searchPlaceholder">
                            {(message) => (
                                <ReactAutocomplete
                                    ref={this.inputRef}
                                    wrapperProps={{
                                        className: 'lvo-search-input__search-input-wrapper',
                                    }}
                                    inputProps={{
                                        className: 'lvo-search-input__search-input',
                                        placeholder: message.toString(),
                                        autoFocus: true,
                                    }}
                                    value={value}
                                    onChange={this.handleSearchChange}
                                    onSelect={this.handleSuggestSelect}
                                    autoHighlight={true}
                                    selectOnBlur={false}
                                    items={suggestions as string[]}
                                    getItemValue={identity}
                                    renderMenu={this.renderSuggestionMenu}
                                    renderItem={this.renderSuggestionItem}
                                />
                            )}
                        </FormattedMessage>
                        <div className="lvo-search-input__show-all">
                            <button type="submit">
                                <FormattedMessage id="searchInput_send"/>
                            </button>
                            <Link to="?" replace={true}>
                                <FormattedMessage id="searchInput_clear"/>
                            </Link>
                        </div>
                    </div>
                    <div className="lvo-search-input__amount">
                        {total !== null
                            ? <i><FormattedMessage id="searchInput_amount" values={{ total }}/></i>
                            : null}
                    </div>
                </form>
            </div>
        );
    }

    @bind
    private handleSearchChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const { onChange } = this.props;
        onChange(event.target.value);
    }

    @bind
    private handleSuggestSelect(value: string, suggest: string): void {
        const { onSubmit } = this.props;
        this.blur();
        onSubmit(suggest);
    }

    @bind
    private handleSearchSubmit(event: React.FormEvent<HTMLFormElement>): void {
        event.preventDefault();

        const { onSubmit, value } = this.props;
        this.blur();
        onSubmit(value);
    }

    @bind
    private renderSuggestionMenu(menuItems: ReadonlyArray<React.ReactNode>): JSX.Element {
        const { suggestions } = this.props;

        return (
            <div className="lvo-search-input__suggestion-menu" hidden={!suggestions.length}>
                {menuItems}
            </div>
        );
    }

    @bind
    private renderSuggestionItem(suggestion: string, isHighlighted: boolean): JSX.Element {
        const className = classList('lvo-search-input__suggestion-item', {
            'lvo-search-input__suggestion-item--active': isHighlighted,
        });

        return (
            <div key={suggestion} className={className}>
                {suggestion}
            </div>
        );
    }

    private blur(): void {
        if (this.inputRef && this.inputRef.current) {
            this.inputRef.current.blur();
        }
    }
}
